import React from 'react'
import { useNavigate } from 'react-router'

import { NEWS_UPDATES_CONSTANTS } from './constants'
import ThumbnailFallback from './ThumbnailFallback'
import { useImageFallback } from './useImageFallback'
import { trackClickEvent } from '../../analytics/gtm'
import ThumbnailSvg from '../../assets/homepage/ThumbnailSvg.png'
import backarrow from '../../assets/icons/backarrow.svg'
import { NewsUpdatesApiResponse } from '../../model/NewsUpdatesApiResponse'
import ImageWithFallback from '../ImageWithFallback/ImageWithFallback'
interface NewsModuleCardProps {
  update: NewsUpdatesApiResponse
}

const NewsModuleCard = ({ update }: NewsModuleCardProps) => {
  const navigate = useNavigate()
  const { isImageError } = useImageFallback()

  const pushClick = (payload: Parameters<typeof trackClickEvent>[0]): void => {
    trackClickEvent(payload)
  }

  function handleCtaClick(): void {
    pushClick({
      module: 'news module',
      module_title: 'latest',
      click_text: update.buttonText ?? '',
      click_url: '/ideas-bank',
      click_element: 'button',
    })
    navigate('/ideas-bank')
  }

  return (
    <article
      className="w-[inherit] inline-flex items-center gap-[24px] p-6 rounded-[40px] bg-white/60 shadow-[0_2px_8px_0_rgba(0,0,0,0.10)] backdrop-blur-[10px]"
      onClick={() => {
        pushClick({
          module: 'news module',
          module_title: 'latest',
          click_text: update.title,
          click_url: '/ideas-bank',
          click_element: 'card',
        })
        navigate('/ideas-bank')
      }}
    >
      {/* Thumbnail */}

      {isImageError ? (
        <ThumbnailFallback title={update.title} />
      ) : (
        <div
          className={`
               relative h-[262px] shrink-0 rounded-2xl border-2 border-white
              overflow-hidden
            `}
        >
          <ImageWithFallback
            src={update.image}
            fallbackSrc={ThumbnailSvg}
            alt={update.title}
            className="w-[464px] h-full object-cover object-center w-full h-full rounded-2xl"
            loading="lazy"
          />
          {/* Gradient overlay (matches mos-pg-ideasbank HighlightedCard .gradient-overlay) */}
          <div
            className="absolute bottom-0 left-0 w-full h-[120px] pointer-events-none z-[2]"
            style={{
              background:
                'linear-gradient(to top, rgba(0, 0, 0, 0.3) 0%, rgba(0, 0, 0, 0.18) 30%, rgba(0, 0, 0, 0.06) 60%, rgba(0, 0, 0, 0) 100%)',
            }}
            aria-hidden="true"
          />
          {update.isLatest && (
            <div className="absolute top-4 left-4">
              <span
                className="flex items-center justify-center gap-1 rounded-lg bg-[#505050] p-2 subtitle-xs text-color-neutral-10"
                aria-label="Latest update"
              >
                {NEWS_UPDATES_CONSTANTS.LATEST_TAG}
              </span>
            </div>
          )}
        </div>
      )}

      {/* Content */}
      <div className="flex flex-col items-start gap-6 shrink-0">
        <div className="flex flex-col items-start gap-1 self-stretch w-[440px] min-[1440px]:w-[750px]">
          <span
            className="text-color-neutral-80 subtitle-xs uppercase"
            aria-label={`Category: ${NEWS_UPDATES_CONSTANTS.PAGE_NAME}`}
          >
            {update.type}
          </span>
          <h3 className="headline-s text-color-neutral-100 line-clamp-3">{update.title}</h3>
          <p className="text-color-neutral-80 body-m line-clamp-2">{update.description}</p>
        </div>

        {update.buttonText && (
          <div className="">
            <button
              onClick={handleCtaClick}
              className={`flex h-10 min-w-[96px] items-center justify-center gap-2 rounded-[100px] px-4 py-[10px],
                bg-black hover:bg-[#6E6E6E] text-white subtitle-s
              `}
              aria-label={`${update.buttonText}: ${update.title}`}
              type="button"
            >
              {update.buttonText}
              <img src={backarrow} alt="ideas arrow" width="20" height="20" className="rotate-180" />
            </button>
          </div>
        )}
      </div>
    </article>
  )
}

export default NewsModuleCard
